import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GeometryType } from "@evergis/api";
import { useTranslation } from "react-i18next";

import { GeometryEditorMode, setGeometryEditor } from "../../../ducks/geometryEditor/geometryEditor";
import { getGeometryEditor } from "../../../ducks/geometryEditor/selectors";
import { toggleMeasurer } from "../../../ducks/measurer/measurer";
import { setMouseLabel } from "../../../ducks/mouseLabel/mouseLabel";
import { getCurrentFeatureId } from "../../../ducks/selectFeatures/selectors";
import { setSnappingEnabled } from "../../../ducks/snapping/snapping";
import { useResetFeatureCard } from "../../FeatureCard/hooks/useResetFeatureCard";
import { useResetGeometryEditor } from "./useResetGeometryEditor";
import { useLimitsExceededCheck } from "../../../hooks/useLimitsExceededCheck";
import { useUploadDataDialog } from "../../../hooks/useDialogs";
import { usePostgresLayer } from "../../../hooks/layer/usePostgresLayer";
import { useGeometrySelectionContext } from "../../../hooks/geometrySelection/useGeometrySelectionContext";
import { useGeometrySelection } from "../../../hooks/geometrySelection/useGeometrySelection";
import { useAction } from "../../../hooks/useAction";
import { closeBottomContainer } from "../../../ducks/layout/layout";
import { resetGeometrySelection } from "../../../ducks/geometrySelection/geometrySelection";

import { UsageGeometryType } from "../../../interfaces/geometryType";
import { EditGeometryType } from "../../../evergis/core/attributes/geometryTypes";

// eslint-disable-next-line max-lines-per-function
export const useFeatureCreator = (
  layerName: string,
  geometryType: GeometryType | EditGeometryType,
): [boolean, VoidFunction] => {
  const { active, layerName: activeLayerName } = useSelector(getGeometryEditor);
  const resetGeometryEditor = useResetGeometryEditor();
  const selectedFeature = useSelector(getCurrentFeatureId);
  const editorActive = active && activeLayerName === layerName && selectedFeature === null;
  const processedUploadData = useLimitsExceededCheck(useUploadDataDialog(), true);
  const { postgresLayer } = usePostgresLayer(layerName, { waitInput: true });
  const dispatch = useDispatch();
  const uploadDataDialog = useResetFeatureCard(() => processedUploadData([layerName]), [dispatch]);
  const { t } = useTranslation("layerList");
  const { layer: geometrySelectionLayer } = useGeometrySelectionContext();
  const [geometrySelection] = useGeometrySelection();
  const closeTable = useAction(closeBottomContainer);

  const featureCreateMouseLabel: Record<UsageGeometryType, string> = useMemo(
    () => ({
      [GeometryType.Multipoint]: t("clickToSetMultiPoint"),
      [EditGeometryType.Raster]: t("uploadData"),
    }),
    [t],
  );

  const createFeature = useResetFeatureCard(() => {
    const { active: geometrySelectionActive, history: geometrySelectionHistory } = geometrySelection;

    if (geometrySelectionActive) {
      if (geometrySelectionHistory.step >= 0) {
        geometrySelectionLayer.remove(geometrySelectionHistory.features[geometrySelectionHistory.step]);
      }

      dispatch(resetGeometrySelection());

      closeTable();
    }

    if (postgresLayer.type === "PostgresTileLayerService") {
      uploadDataDialog();
    } else {
      resetGeometryEditor();

      if (!active || activeLayerName !== layerName) {
        const actualGeometryType = geometryType || postgresLayer.geometryType;

        dispatch(setGeometryEditor({ geometryType: actualGeometryType, layerName, mode: GeometryEditorMode.Edit }));
        dispatch(toggleMeasurer(false));
        dispatch(setMouseLabel(featureCreateMouseLabel[actualGeometryType] || null));
        dispatch(setSnappingEnabled(true));
      }
    }
  }, [
    geometrySelection,
    geometrySelectionLayer,
    postgresLayer,
    geometryType,
    layerName,
    active,
    activeLayerName,
    dispatch,
    featureCreateMouseLabel,
  ]);

  return [editorActive, createFeature];
};
